import { OrgNode, OrgUnit, ErrorItem, FailedRolloutItem } from './rollout.interface';
import { RolloutStatus } from './rollout.const';
import { LogType } from '@swagger/humanresources';
import { ListResponseArgsOfAsyncTaskLogInfoDto } from 'src/swagger/humanresources/models/list-response-args-of-async-task-log-info-dto';

let branchesCount = 0;

/**
 * Determines rollout status based on latest milestone (LogType)
 * This is the fallback when no log response data is available
 */
export function getRolloutStatusFromMilestone(latestMilestone: number): RolloutStatus {
  /**
   * Check when latestMilestone is written from the backend
   */
  if (latestMilestone === null) {
    return RolloutStatus.IDLE;
  }
  switch (latestMilestone) {
    case LogType.Succeeded: // 1
      return RolloutStatus.SUCCESS;
    case LogType.Failed: // 2
    case LogType.Aborted: // 4
      return RolloutStatus.ERROR;
    default:
      // return RolloutStatus.ERROR;
      // return RolloutStatus.IDLE;
      return RolloutStatus.LOADING;
  }
}

/**
 * Determines rollout status based on actual successful/failed results
 * This provides more accurate status than just milestone
 * @param successful - Array of successful rollout items
 * @param failed - Array of failed rollout items
 * @param latestMilestone - Fallback milestone if no result data
 * @returns RolloutStatus based on actual results
 */
export function getRolloutStatusFromResults(
  successful: unknown[] | undefined,
  failed: unknown[] | undefined,
  latestMilestone: number,
): RolloutStatus {
  // If no result data is available, fall back to milestone-based status
  if (!successful && !failed) {
    return getRolloutStatusFromMilestone(latestMilestone);
  }

  const successCount = successful?.length || 0;
  const failedCount = failed?.length || 0;
  const totalCount = successCount + failedCount;

  // If no items were processed at all, check milestone
  if (totalCount === 0) {
    return getRolloutStatusFromMilestone(latestMilestone);
  }

  // Determine status based on success/failure distribution
  if (failedCount === 0) {
    // All successful - SUCCESS
    return RolloutStatus.SUCCESS;
  } else if (successCount === 0) {
    // All failed - ERROR
    return RolloutStatus.ERROR;
  } else {
    // Mixed results - WARNING (partial success)
    return RolloutStatus.WARNING;
  }
}

export function getBranches(): OrgUnit[] {
  const storedOUs = sessionStorage.getItem('ous');
  let result = [];
  if (storedOUs) {
    const parsedOUs = JSON.parse(storedOUs);
    result = collectBranches(parsedOUs);
  }
  if (result.length === 0) {
    console.warn('No organizational units found. Branch list will be empty.', branchesCount);
    setTimeout(() => {
      if (branchesCount < 5) {
        branchesCount = branchesCount + 1;
        getBranches();
      }
    }, 1000);
  } else {
    branchesCount = 0;
  }
  return result.map((branch) => {
    let branchValue = null;
    if (branch?.data) {
      branchValue = branch.data;
    } else {
      branchValue = branch;
    }
    branchValue = { ...branchValue, lastRolloutStatus: RolloutStatus.IDLE };
    return branchValue;
  });
}

export function collectBranches(nodes: OrgNode[] | OrgUnit[]): OrgNode[] {
  const result: OrgNode[] = [];
  for (const node of nodes) {
    const currentData = 'data' in node ? node.data : (node as OrgUnit);
    const children = currentData.children ?? ('children' in node ? node.children : undefined);

    if (currentData.type === 'BRANCH') {
      result.push(node as OrgNode);
    }
    if (Array.isArray(children)) {
      result.push(...collectBranches(children));
    }
  }

  return result;
}

/**
 * Safely retrieves and parses running rollout UIDs from localStorage
 * @returns Array of running rollout UIDs, empty array if parsing fails
 */
export function getRunningRolloutUIds(): string[] {
  try {
    const stored = localStorage.getItem('runningRolloutUIds');
    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);

    // Validate that parsed data is an array of strings
    if (!Array.isArray(parsed)) {
      console.warn('Invalid runningRolloutUIds format: not an array, resetting to empty array');
      localStorage.setItem('runningRolloutUIds', JSON.stringify([]));
      return [];
    }

    // Filter out non-string values
    const validUIds = parsed.filter((uid) => typeof uid === 'string' && uid.trim() !== '');

    // If filtered array is different, update localStorage
    if (validUIds.length !== parsed.length) {
      console.warn('Invalid UIDs found in localStorage, cleaning up');
      localStorage.setItem('runningRolloutUIds', JSON.stringify(validUIds));
    }

    return validUIds;
  } catch (error) {
    console.error('Failed to parse runningRolloutUIds from localStorage:', error);
    console.warn('Resetting runningRolloutUIds to empty array');

    // Reset to valid empty array on parse error
    localStorage.setItem('runningRolloutUIds', JSON.stringify([]));
    return [];
  }
}

/**
 * Safely adds a rollout UID to the running operations list
 * @param uid - The rollout operation UID to add
 */
export function addRunningRolloutUId(uid: string): void {
  if (!uid || typeof uid !== 'string' || uid.trim() === '') {
    console.warn('Invalid UID provided for rollout tracking:', uid);
    return;
  }

  try {
    const existingUIds = getRunningRolloutUIds();

    if (!existingUIds.includes(uid)) {
      existingUIds.push(uid);
      localStorage.setItem('runningRolloutUIds', JSON.stringify(existingUIds));
    }
  } catch (error) {
    console.error('Failed to add running rollout UID:', error);
    // Attempt to create new array with just the current UID
    try {
      localStorage.setItem('runningRolloutUIds', JSON.stringify([uid]));
    } catch (fallbackError) {
      console.error('Failed to create new runningRolloutUIds array:', fallbackError);
    }
  }
}

/**
 * Safely removes a rollout UID from the running operations list
 * @param uid - The rollout operation UID to remove
 */
export function removeRunningRolloutUId(uid: string): void {
  if (!uid || typeof uid !== 'string') {
    console.warn('Invalid UID provided for removal:', uid);
    return;
  }

  try {
    const existingUIds = getRunningRolloutUIds();
    const updatedUIds = existingUIds.filter((id) => id !== uid);
    localStorage.setItem('runningRolloutUIds', JSON.stringify(updatedUIds));
  } catch (error) {
    console.error('Failed to remove running rollout UID:', error);
  }
}

/**
 * Extracts error data from rollout log response for error view component
 * @param logResponse - The log response containing jsonMessage with rollout results
 * @returns Array of ErrorItem objects for display in error view
 */
export function extractRolloutErrors(
  logResponse: ListResponseArgsOfAsyncTaskLogInfoDto,
): ErrorItem[] {
  try {
    // Validate response structure
    if (!logResponse?.result?.length) {
      console.warn('Invalid log response: no result array found');
      return [];
    }

    const firstResult = logResponse.result[0];
    if (!firstResult?.jsonMessage) {
      console.warn('Invalid log response: no jsonMessage found');
      return [];
    }

    // Parse the stringified JSON message
    const parsedMessage = JSON.parse(firstResult.jsonMessage);

    // Extract Failed array
    const failedItems = parsedMessage?.Failed;
    if (!Array.isArray(failedItems)) {
      console.warn('No failed items found in rollout response');
      return [];
    }

    // Transform failed items to ErrorItem format
    const errors: ErrorItem[] = failedItems
      .map((failedItem: FailedRolloutItem) => {
        const result = failedItem?.Result;
        if (!result) {
          console.warn('Invalid failed item: no Result object found', failedItem);
          return null;
        }

        // Combine firstName and lastName for employeeName
        const firstName = result.FirstName || '';
        const lastName = result.LastName || '';
        const employeeName = `${firstName} ${lastName}`.trim();

        return {
          employeeName: employeeName || 'Unknown Employee',
          employeeId: result.EmployeeNumber || 'Unknown ID',
          errorMessage: failedItem.Message || 'Unknown error occurred',
        };
      })
      .filter((error): error is ErrorItem => error !== null); // Filter out null values
    return errors;
  } catch (error) {
    console.error('Failed to extract rollout errors:', error);
    return [];
  }
}
